/**
 * Mars-portfolio-specific chart helpers. Color palettes, hex/alpha and
 * window math live in `@/lib/plotly/colors` and `@/lib/utils/timeframe` and
 * are imported directly by the components.
 */
import { getWindowRange } from '@/lib/utils/timeframe';
import type { Timeframe } from '../types';

export interface SlicedSeries<T> {
  dates: string[];
  series: T[][];
}

/**
 * Match `MarsPortfolioDashboard.sliceByHorizon`. Anchored at the last
 * date in `dates`; uses canonical `getWindowRange` for the window math.
 */
export function sliceByHorizon<T>(
  dates: string[],
  seriesList: T[][],
  timeframe: Timeframe,
): SlicedSeries<T> {
  if (dates.length === 0) return { dates: [], series: seriesList.map(() => []) };

  const lastDate = dates[dates.length - 1];
  const [start] = getWindowRange(lastDate, timeframe);
  const startMs = new Date(start).getTime();

  let idx = 0;
  for (let i = dates.length - 1; i >= 0; i--) {
    if (new Date(dates[i]).getTime() < startMs) {
      idx = i + 1;
      break;
    }
  }

  return {
    dates: dates.slice(idx),
    series: seriesList.map((arr) => arr.slice(idx)),
  };
}

export interface TickConfig {
  tickformat: string;
  dtick: string;
}

/**
 * Risk-metrics x-axis tick config keyed off years of data. Diverges from the
 * canonical `tickConfigForTimeframe` in the short-window bucket (M6 vs M2)
 * to match `MarsPortfolioRiskMetricsManager.getXAxisTickConfig`.
 */
export function getXAxisTickConfig(timeRangeYears: number): TickConfig {
  if (timeRangeYears >= 25) return { tickformat: '%Y', dtick: 'M60' };
  if (timeRangeYears >= 15) return { tickformat: '%Y', dtick: 'M24' };
  if (timeRangeYears >= 4) return { tickformat: '%Y', dtick: 'M12' };
  return { tickformat: '%b-%y', dtick: 'M6' };
}

/**
 * Exposures-chart tick config -- thresholds and short-window dtick differ
 * from the risk-metrics and canonical variants.
 */
export function getExposureTickConfig(timeRangeYears: number): TickConfig {
  if (timeRangeYears >= 15) return { tickformat: '%Y', dtick: 'M24' };
  if (timeRangeYears >= 8) return { tickformat: '%Y', dtick: 'M12' };
  return { tickformat: '%b-%y', dtick: 'M2' };
}

/** Percent formatter used everywhere ("1.23%", "-"). */
export function fmtPct(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return `${value.toFixed(digits)}%`;
}

export function fmtRatio(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return value.toFixed(digits);
}

/** Maps display name back to API data key. */
export const ASSET_DATA_KEYS: Record<string, string> = {
  Equities: 'Equities',
  'Fixed Income': 'Bonds',
  Bonds: 'Bonds',
  Commodities: 'Commodities',
  'Inflation Swaps': 'Inflation',
  Inflation: 'Inflation',
};

/**
 * Short fund identifier used in the prose description on the Asset Positions
 * tab.
 */
function fundShortName(fundId: string): string {
  switch (fundId) {
    case 'mars-qmas':
      return 'QMAS';
    case 'mars-qmas-nn':
      return 'QMAS NN';
    case 'mars-dar-nn':
      return 'DAR NN';
    case 'mars-dar':
      return 'DAR';
    case 'mat-ucits':
      return 'MAT UCITS';
    case 'mars-fqm':
      return 'FQM';
    case 'mars-fidelity-balanced':
      return 'Balanced Portfolio';
    case 'mars-fidelity-conservative':
      return 'Conservative Portfolio';
    default:
      return 'the selected portfolio';
  }
}

/** Build the Asset Positions description shown above the chart. */
export function buildAssetPositionsDescription(fundId: string): string {
  return (
    `This page shows the asset-class positions for MARS in ${fundShortName(fundId)}. ` +
    'These are generated by using advanced AI techniques to train a fictitious agent ' +
    'that maximizes a utility-based measure of wealth over multiple periods in the face ' +
    'of changing markets. By working with the full distribution of returns implied by ' +
    'the MARS forecasting model, the framework captures uncertainty, risk, and ' +
    'non-linear relationships between macroeconomic variables, market conditions and ' +
    'asset returns.'
  );
}
